import React, { useEffect, useMemo, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { Base64 } from 'js-base64';

import { FunctionalRootState, getPreferenceFileUrlFromAccount, UrlUtils } from '@share/utils';
import { change, ILoginState, IPasswordState, setLoginRedirect } from '@share/store/slices';

import AshevillePlayMain from '@assets/images/ashevilleplay/asheville-hero.jpg';

import './style.scss';
import { FormattedMessage } from 'react-intl';
import { Spin } from 'antd';
import { BACKGROUND_PHOTO_FIELD_KEY, IAccount } from '@share/common-types';

const REDIRECT_QUERY_PARAM = 'redirect';
const SEARCH_QUERY_PARAM = 'search';

export function ChangePasswordAshevillePlay() {

  const loginStore: ILoginState = useSelector((state: FunctionalRootState) => state.loginStore);
  const passwordStore: IPasswordState = useSelector((state: FunctionalRootState) => state.passwordStore);

  const { account } = loginStore;
  const { loading } = passwordStore;

  const dispatch = useDispatch();

  const redirect = useMemo(() => UrlUtils.getSearchParam(REDIRECT_QUERY_PARAM), []);
  const search = useMemo(() => UrlUtils.getSearchParam(SEARCH_QUERY_PARAM), []);

  useEffect(() => {
    if (redirect) {
      dispatch(setLoginRedirect(`${redirect}${search ? `?${Base64.decode(search)}` : ''}`));
    }
  }, [redirect]);

  const [formData, setFormData] = useState({
    current: '',
    password: '',
  });
  
  const [errors, setErrors] = useState<{
    current?: string;
    password?: string;
    general?: string;
  }>({});
    
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };
  
  const validateForm = () => {
    const newErrors: typeof errors = {};
    
    if (!formData.current) {
      newErrors.current = 'Current password is required';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
        
    dispatch(change(formData.current, formData.password, true));
  };
  
  const backgroundImage = getPreferenceFileUrlFromAccount(account as IAccount, BACKGROUND_PHOTO_FIELD_KEY);
  const image = backgroundImage ? backgroundImage : AshevillePlayMain;

  return (
    <div className="login-page-asheville-play change-password-asheville-play" style={{ backgroundImage: `linear-gradient(135deg, rgba(26, 26, 26, 0.4) 0%,rgba(26, 26, 26, 0.2) 100%), url(${image})` }}>
      <div className="login-card">
        <div className="login-card-header">
          <div className="logo-container">
            <h1 className="logo-text">Asheville Play</h1>
            <p className="logo-subtitle">Your Asheville starts here</p>
          </div>
          <h4>Change Password</h4>
        </div>
        
        <div style={{ marginBottom: '1rem' }}>
          <form onSubmit={handleSubmit}>
            <div className="input-group">
              <label className="input-label" htmlFor="current"><FormattedMessage id="current.password" /></label>
              <input
                id="current"
                name="current"
                type="password"
                value={formData.current}
                onChange={handleInputChange}
                placeholder="Enter current password"
                className={`input-email-field ${errors.current ? 'error' : ''}`}
              />
              {errors.current && <span className="input-error">{errors.current}</span>}
            </div>
            
            <div className="input-group">
              <label className="input-label" htmlFor="password"><FormattedMessage id="new.password" /></label>
              <input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Enter your password"
                className={`input-password-field ${errors.password ? 'error' : ''}`}
              />
              {errors.password && <span className="input-error">{errors.password}</span>}
            </div>
            
            {errors.general && (
              <span className="input-error" style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
                {errors.general}
              </span>
            )}
            
            <button className="full-width-button" type="submit" disabled={loading}>
              {loading ? <Spin size="small" style={{ marginRight: '10px' }} /> : null} Submit
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};
